import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ru from './locales/ru.json';
import en from './locales/en.json';

/**
 * i18n bootstrap for the authenticated app.
 *
 * - Two resource languages (ru, en) with ru as both default and
 *   fallback. KZ is not a UI locale yet — the toggle is ru/en only.
 * - Two namespaces split by concern: `common` (shell chrome,
 *   navigation, shared buttons) and `settings` (settings pages).
 *   Extra namespaces can be added later without touching the init.
 * - Detector order is explicit: localStorage key `altai-lang` first
 *   (matches the existing LangToggle write path), then navigator,
 *   then fallback. Caching back into localStorage keeps the toggle
 *   and i18next in sync when the user flips language.
 * - escapeValue is disabled because React already escapes
 *   interpolated values — turning it on double-escapes apostrophes.
 */
export const SUPPORTED_LANGUAGES = ['ru', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ru: {
        common: ru.common,
        settings: ru.settings,
        dashboard: ru.dashboard,
        company: ru.company,
        workspace: ru.workspace,
      },
      en: {
        common: en.common,
        settings: en.settings,
        dashboard: en.dashboard,
        company: en.company,
        workspace: en.workspace,
      },
    },
    ns: ['common', 'settings', 'dashboard', 'company', 'workspace'],
    defaultNS: 'common',
    fallbackLng: 'ru',
    supportedLngs: ['ru', 'en'],
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'altai-lang',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    returnNull: false,
  });

export default i18n;
